import {
  Dropzone,
  Spinner,
  Text,
  Title,
} from "@appquality/appquality-design-system";
import { useEffect, useMemo, useState } from "react";
import { FileEarmarkFill, PlayCircleFill, Trash } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import siteWideMessageStore from "src/redux/siteWideMessages";
import {
  FileExtBadge,
  FileNameLabel,
  ImageThumbButton,
  MediaFileTile,
  MediaGrid,
  MediaTile,
  RemoveMediaButton,
  Section,
  VideoOverlay,
  VideoThumbnail,
} from "../styled";

const getCleanFilename = (url: string): string => {
  try {
    return new URL(url).pathname.split("/").pop() || url;
  } catch {
    return url.split("?")[0].split("/").pop() || url;
  }
};

const getExtension = (filename: string): string => {
  const ext = filename.split(".").pop();
  return ext && ext.length <= 5 ? ext : "file";
};
import { Lightbox } from "./Lightbox";
import { BugData } from "./types";
import { LoadingContainer } from "../styled";

interface Props {
  data: BugData;
  isEditing: boolean;
  isUploading: boolean;
  mediaToRemove: number[];
  newMediaFiles: File[];
  totalMediaCount: number;
  isMediaValid: boolean;
  minimumMedia: number;
  validFileExtensions?: string[];
  onToggleRemove: (mediaId: number) => void;
  onAddFiles: (files: File[]) => void;
  onRemoveNewFile: (index: number) => void;
}

const BugMediaSection = ({
  data,
  isEditing,
  isUploading,
  mediaToRemove,
  newMediaFiles,
  totalMediaCount,
  isMediaValid,
  minimumMedia,
  validFileExtensions,
  onToggleRemove,
  onAddFiles,
  onRemoveNewFile,
}: Props) => {
  const { t } = useTranslation();
  const { add } = siteWideMessageStore();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const newMediaPreviews = useMemo(
    () =>
      newMediaFiles.map((file) => ({
        file,
        url: URL.createObjectURL(file),
      })),
    [newMediaFiles],
  );

  useEffect(
    () => () => {
      newMediaPreviews.forEach(({ url }) => URL.revokeObjectURL(url));
    },
    [newMediaPreviews],
  );

  const lightboxItems = (data.media ?? [])
    .filter((m) => !mediaToRemove.includes(m.id))
    .filter((m) => m.type.includes("image") || m.type.includes("video"));

  const hasMedia =
    (data.media && data.media.length > 0) || newMediaFiles.length > 0;
  if (!hasMedia && !isEditing) return null;

  return (
    <>
      <Section>
        <Title size="s" className="aq-mb-2">
          {t("Media")}
        </Title>

        {hasMedia && (
          <MediaGrid>
            {data.media
              ?.filter((media) => !mediaToRemove.includes(media.id))
              .map((media) => (
                <MediaTile key={media.id}>
                  {media.type.includes("image") ? (
                    <ImageThumbButton
                      type="button"
                      aria-label={`Open image ${media.id}`}
                      disabled={isEditing}
                      onClick={() => {
                        if (isEditing) return;
                        const idx = lightboxItems.findIndex(
                          (m) => m.id === media.id,
                        );
                        if (idx !== -1) {
                          setLightboxIndex(idx);
                          setLightboxOpen(true);
                        }
                      }}
                    >
                      <img src={media.url} alt={`Bug media ${media.id}`} />
                    </ImageThumbButton>
                  ) : media.type.includes("video") ? (
                    <VideoThumbnail
                      $clickable={!isEditing}
                      onClick={() => {
                        if (isEditing) return;
                        const idx = lightboxItems.findIndex(
                          (m) => m.id === media.id,
                        );
                        if (idx !== -1) {
                          setLightboxIndex(idx);
                          setLightboxOpen(true);
                        }
                      }}
                    >
                      <video
                        src={media.url}
                        preload="metadata"
                        muted
                        playsInline
                      />
                      <VideoOverlay>
                        <PlayCircleFill size={36} />
                      </VideoOverlay>
                    </VideoThumbnail>
                  ) : (
                    <MediaFileTile
                      href={media.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={getCleanFilename(media.url)}
                      aria-disabled={isEditing ? "true" : undefined}
                      onClick={
                        isEditing ? (e) => e.preventDefault() : undefined
                      }
                    >
                      <FileEarmarkFill size={36} />
                      <FileExtBadge>
                        {getExtension(getCleanFilename(media.url))}
                      </FileExtBadge>
                      <FileNameLabel>
                        {getCleanFilename(media.url)}
                      </FileNameLabel>
                    </MediaFileTile>
                  )}
                  {isEditing && (
                    <RemoveMediaButton
                      type="button"
                      onClick={() => onToggleRemove(media.id)}
                      title={t("Remove")}
                      aria-label={t("Remove")}
                    >
                      <Trash size={14} />
                    </RemoveMediaButton>
                  )}
                </MediaTile>
              ))}

            {!isUploading &&
              newMediaPreviews.map(({ file, url }, index) => (
              <MediaTile key={`new-${index}`}>
                {file.type.includes("video") ? (
                  <VideoThumbnail $clickable={false}>
                    <video src={url} preload="metadata" muted playsInline />
                    <VideoOverlay>
                      <PlayCircleFill size={36} />
                    </VideoOverlay>
                  </VideoThumbnail>
                ) : file.type.includes("image") ? (
                  <ImageThumbButton
                    type="button"
                    disabled
                    aria-label={file.name}
                  >
                    <img src={url} alt={file.name} />
                  </ImageThumbButton>
                ) : (
                  <MediaFileTile aria-disabled="true">
                    <FileEarmarkFill size={36} />
                    <FileExtBadge>{getExtension(file.name)}</FileExtBadge>
                    <FileNameLabel>{file.name}</FileNameLabel>
                  </MediaFileTile>
                )}
                <RemoveMediaButton
                  type="button"
                  onClick={() => onRemoveNewFile(index)}
                  title={t("Remove")}
                  aria-label={t("Remove")}
                >
                  <Trash size={14} />
                </RemoveMediaButton>
              </MediaTile>
            ))}
          </MediaGrid>
        )}

        {isUploading && newMediaFiles.length > 0 && (
          <LoadingContainer>
            <Spinner />
          </LoadingContainer>
        )}

        {isEditing && !isUploading && (
          <>
            <div className="aq-mt-3">
              <Dropzone
                description={t(
                  "Click here to upload your files or drag and drop!",
                )}
                accept={validFileExtensions}
                onAccepted={(files) => onAddFiles(files)}
                onRejected={() =>
                  add({
                    type: "danger",
                    message: t("Invalid file type."),
                    expire: 5,
                  })
                }
              />
            </div>
            {!isMediaValid && (
              <Text className="aq-text-danger aq-mt-2">
                {t("At least {{count}} media files are required", {
                  count: minimumMedia,
                })}{" "}
                ({totalMediaCount}/{minimumMedia})
              </Text>
            )}
          </>
        )}
      </Section>
      <Lightbox
        images={lightboxItems}
        startIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  );
};

export default BugMediaSection;
